import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSun, faMoon, faBars, faTimes, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import SupportEmailModal from '../../../screens/profile/components/SupportEmailModal';
import { AuthContext } from '../../../context/AuthProvider';

const Header = ({ logo, navItems, onThemeChange, currentTheme, theme }) => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [isContactOpen, setIsContactOpen] = React.useState(false);
  const { isAuthenticated } = useContext(AuthContext);

  // Create custom styles for hover effects
  const navItemStyle = {
    color: theme?.text?.primary,
    transition: 'color 0.2s ease-in-out',
  };

    const signInButtonStyle = {
      backgroundColor: theme?.primary?.blue?.[500],
    color: theme?.neutral?.absolute_white,
    transition: 'background-color 0.2s ease-in-out',
  };

  const contactButtonStyle = {
    color: theme?.text?.primary,
    transition: 'color 0.2s ease-in-out',
  };

  // Theme toggle switch styles
  const toggleSwitchStyle = {
    width: '48px',
    height: '24px',
    backgroundColor: currentTheme === 'dark' ? '#374151' : '#D1D5DB',
    borderRadius: '12px',
    position: 'relative',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    padding: '2px',
  };

  const toggleThumbStyle = {
    width: '20px',
    height: '20px',
    backgroundColor: '#FFFFFF',
    borderRadius: '50%',
    position: 'absolute',
    left: currentTheme === 'dark' ? 'calc(100% - 22px)' : '2px',
    transition: 'left 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    border: '1px solid #E5E7EB',
  };

  // Check if mobile banner is visible (using sessionStorage and device detection)
  const [bannerOffset, setBannerOffset] = React.useState(0);
  
  React.useEffect(() => {
    const checkBannerVisibility = () => {
      const isDismissed = sessionStorage.getItem('mobileAppBannerDismissed');
      if (isDismissed === 'true') {
        setBannerOffset(0);
        return;
      }
      
      // Check if user is on iOS or Android
      const userAgent = navigator.userAgent || '';
      const isIOS = /iPad|iPhone|iPod/.test(userAgent);
      const isAndroid = /Android/.test(userAgent);
      
      if (isIOS || isAndroid) {
        // Banner height is approximately 56px (12px padding top + 12px padding bottom + ~32px content)
        setBannerOffset(56);
      } else {
        setBannerOffset(0);
      }
    };
    
    checkBannerVisibility();
    // Check periodically in case banner is dismissed
    const interval = setInterval(checkBannerVisibility, 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <header 
      className={`shadow-sm fixed w-full z-50`} 
      style={{ 
        backgroundColor: theme?.white,
        top: `${bannerOffset}px`,
        transition: 'top 0.3s ease'
      }}
    >
      <style>
        {`
          .nav-item:hover {
            color: ${theme?.primary?.blue?.[500] || '#3B82F6'} !important;
          }
          .sign-in-button:hover {
            background-color: ${theme?.primary?.blue?.[600] || '#2563EB'} !important;
          }
        `}
      </style>
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-14">
          <Link to="https://www.learnzapp.com" className="flex-shrink-0">
            <img
              src={logo}
              alt="LearnZapp"
              className="h-6 w-auto ml-2"
            />
          </Link>

          {/* Desktop navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navItems && navItems.length > 0 && navItems.map((item, index) => (
              <a
                key={item.id || `nav-item-${index}`}
                href={item.url}
                className="px-3 py-2 text-sm font-medium nav-item"
                style={navItemStyle}
              >
                {item.label}
              </a>
            ))}
            <button
              onClick={() => setIsContactOpen(true)}
              className="px-3 py-2 text-sm font-medium nav-item flex items-center gap-2"
              style={contactButtonStyle}
            >
              <FontAwesomeIcon icon={faEnvelope} />
              Contact
            </button>
            <Link
              to="/home"
              className="px-4 py-2 text-sm font-medium rounded-lg sign-in-button"
              style={signInButtonStyle}
            >
              {isAuthenticated ? 'Go to Exam Prep' : 'Sign In'}
            </Link>
            
            {/* Theme Toggle Switch */}
            <div
              style={toggleSwitchStyle}
              onClick={onThemeChange}
              className="flex-shrink-0"
            >
              <div style={toggleThumbStyle}>
                <FontAwesomeIcon
                  icon={currentTheme === "light" ? faSun : faMoon}
                  className="text-xs"
                  style={{ 
                    color: theme?.primary?.blue?.[500],
                    fontSize: '10px'
                  }}
                />
              </div>
            </div>
          </nav>

          {/* Mobile menu button */}
          <div className="flex md:hidden items-center space-x-4">
            {/* Mobile Theme Toggle Switch */}
            <div
              style={toggleSwitchStyle}
              onClick={onThemeChange}
              className="flex-shrink-0"
            >
              <div style={toggleThumbStyle}>
                <FontAwesomeIcon
                  icon={currentTheme === "light" ? faSun : faMoon}
                  className="text-xs"
                  style={{ 
                    color: currentTheme === 'light' ? '#6B7280' : '#FFFFFF',
                    fontSize: '10px'
                  }}
                />
              </div>
            </div>
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="inline-flex items-center justify-center p-3 rounded-md focus:outline-none focus:ring-2 focus:ring-inset"
              style={{
                color: theme?.text?.primary,
                backgroundColor: theme?.background?.main,
                minWidth: '44px',
                minHeight: '44px'
              }}
            >
              <span className="sr-only">Open main menu</span>
              <FontAwesomeIcon
                icon={isMenuOpen ? faTimes : faBars}
                className="h-5 w-5 sm:h-6 sm:w-6"
              />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile navigation */}
      {isMenuOpen && (
        <div style={{ backgroundColor: theme?.background?.main }}>
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {navItems && navItems.length > 0 && navItems.map((item, index) => (
              <a
                key={item.id || `nav-item-${index}`}
                href={item.href}
                className="block px-3 py-2 text-base font-medium nav-item"
                style={navItemStyle}
                onClick={() => setIsMenuOpen(false)}
              >
                {item.label}
              </a>
            ))}
            <button
              onClick={() => {
                setIsContactOpen(true);
                setIsMenuOpen(false);
              }}
              className="block px-3 py-2 text-base font-medium nav-item flex items-center gap-2"
              style={contactButtonStyle}
            >
              <FontAwesomeIcon icon={faEnvelope} />
              Contact
            </button>
            <Link
              to="/home"
              className="block px-3 py-2 text-base font-medium rounded-lg sign-in-button"
              style={signInButtonStyle}
              onClick={() => setIsMenuOpen(false)}
            >
              {isAuthenticated ? 'Go to Exam Prep' : 'Sign In'}
            </Link>
          </div>
        </div>
      )}

      {/* Contact Dialog */}
      <SupportEmailModal
        isOpen={isContactOpen}
        onClose={() => setIsContactOpen(false)}
        theme={theme}
      />
    </header>
  );
};

export default Header; 